import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Car, User, Clock, Plus, LogOut as Release, Trash2, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";

const KIND_META = {
  lift: { label: "Elevador", cls: "text-primary" },
  bay: { label: "Baia", cls: "text-secondary-foreground" },
  outside: { label: "Baia Externa", cls: "text-amber-600" },
};

const ALERT_STYLE = {
  green: "border-border",
  yellow: "border-amber-500/60 ring-1 ring-amber-500/30",
  red: "border-red-500/60 ring-1 ring-red-500/30",
};

function elapsed(iso, now = Date.now()) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default function FilaMecanicos() {
  const [bays, setBays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAssign, setOpenAssign] = useState(null); // bay id
  const [openConfig, setOpenConfig] = useState(false);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/bays");
    setBays(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const release = async (bayId) => {
    if (!window.confirm("Liberar esta baia?")) return;
    try {
      await api.patch(`/bays/${bayId}/release`);
      toast.success("Baia liberada");
      load();
    } catch { toast.error("Erro ao liberar"); }
  };

  const occupied = bays.filter((b) => b.job).length;
  const alerts = bays.filter((b) => b.alert === "yellow" || b.alert === "red").length;

  return (
    <div className="space-y-6" data-testid="fila-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Chão de fábrica</div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="w-6 h-6 text-primary" /> Fila dos Mecânicos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Elevadores e baias em tempo real. Alerta amarelo &gt;4h · vermelho &gt;8h sem update.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border border-border rounded px-3 py-1.5"><div className="text-[10px] uppercase text-muted-foreground">Ocupadas</div><div className="font-display font-bold">{occupied}/{bays.length}</div></div>
            <div className="border border-border rounded px-3 py-1.5"><div className="text-[10px] uppercase text-muted-foreground">Livres</div><div className="font-display font-bold">{bays.length - occupied}</div></div>
            <div className={`border rounded px-3 py-1.5 ${alerts > 0 ? "border-amber-500/60 text-amber-600" : "border-border"}`}>
              <div className="text-[10px] uppercase text-muted-foreground">Alertas</div><div className="font-display font-bold">{alerts}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-bays-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Dialog open={openConfig} onOpenChange={setOpenConfig}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-bay-btn"><Plus className="w-4 h-4" /> Nova Baia</Button>
            </DialogTrigger>
            <NewBayDialog onCreated={() => { setOpenConfig(false); load(); }} />
          </Dialog>
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Carregando…</div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bays-grid">
          {bays.map((b) => (
            <div key={b.id} className={`border rounded-md bg-card overflow-hidden transition-colors ${ALERT_STYLE[b.alert || "green"]}`} data-testid={`bay-${b.id}`}>
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{KIND_META[b.kind]?.label || "Baia"}</div>
                  <div className="font-display font-bold">{b.name}</div>
                </div>
                {b.job ? (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => release(b.id)} data-testid={`release-${b.id}`}><Release className="w-3.5 h-3.5" /></Button>
                  </div>
                ) : (
                  <Dialog open={openAssign === b.id} onOpenChange={(o) => setOpenAssign(o ? b.id : null)}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid={`assign-${b.id}`}><Plus className="w-3.5 h-3.5" /> Alocar</Button>
                    </DialogTrigger>
                    <AssignDialog bayName={b.name} bayId={b.id} onAssigned={() => { setOpenAssign(null); load(); }} />
                  </Dialog>
                )}
              </div>

              {b.job ? (
                <>
                  <div className="h-32 bg-secondary relative">
                    {b.job.vehicle?.photo_url ?
                      <img src={b.job.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center"><Car className="w-8 h-8 text-muted-foreground" /></div>}
                    {b.alert !== "green" && (
                      <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider flex items-center gap-1 ${b.alert === "red" ? "bg-red-500 text-white" : "bg-amber-500 text-white"}`}>
                        <AlertTriangle className="w-3 h-3" />
                        {b.alert === "red" ? "Parado >8h" : "Parado >4h"}
                      </div>
                    )}
                  </div>
                  {/* Cronômetro grande — atualiza a cada segundo */}
                  <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between" data-testid={`bay-stopwatch-${b.id}`}>
                    <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-widest">
                      <Clock className="w-3.5 h-3.5" />
                      Tempo no {KIND_META[b.kind]?.label?.toLowerCase() || "elevador"}
                    </div>
                    <div className={`font-mono text-2xl font-bold tabular-nums leading-none ${b.alert === "red" ? "text-red-500" : b.alert === "yellow" ? "text-amber-500" : "text-foreground"}`}>
                      {elapsed(b.assigned_at, now) || "00:00:00"}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-display font-semibold text-sm truncate">
                      {b.job.vehicle?.year} {b.job.vehicle?.make} {b.job.vehicle?.model}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{b.job.client_name}</div>
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <span className="truncate text-muted-foreground">{b.job.service_type}</span>
                      <span className="inline-flex items-center gap-1 font-mono">
                        <User className="w-2.5 h-2.5" />{(b.technician || b.job.technician || "—").split(" ")[0]}
                      </span>
                    </div>
                    <div className="mt-2 flex gap-1.5">
                      <button
                        onClick={() => navigate(`/check-status${b.job.vehicle?.plate ? `?plate=${b.job.vehicle.plate}` : ""}`)}
                        className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary hover:bg-accent"
                        data-testid={`bay-status-${b.id}`}
                      >
                        Trocar status <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                      <button
                        onClick={() => navigate(`/estimate/${b.job.id}`)}
                        className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary hover:bg-accent"
                      >
                        Orçamento <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Livre</div>
                  <div className="font-display text-2xl font-bold text-muted-foreground/60 mt-1">—</div>
                  <div className="text-[11px] text-muted-foreground mt-2">Clique em Alocar para colocar um carro nesta baia.</div>
                </div>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}

function NewBayDialog({ onCreated }) {
  const [form, setForm] = useState({ name: "", kind: "lift" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      await api.post("/bays", form);
      toast.success("Baia criada");
      onCreated();
    } catch { toast.error("Erro ao criar"); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="sm:max-w-md" data-testid="new-bay-dialog">
      <DialogHeader><DialogTitle>Nova Baia</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Elevador 5" data-testid="bay-name" /></div>
        <div>
          <Label>Tipo</Label>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="bay-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lift">Elevador</SelectItem>
              <SelectItem value="bay">Baia</SelectItem>
              <SelectItem value="outside">Baia Externa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="bay-save-btn">{saving ? "Salvando…" : "Criar"}</Button>
        </DialogFooter>
      </div>
    </DialogContent>
  );
}

function AssignDialog({ bayName, bayId, onAssigned }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [technician, setTechnician] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => {
      const activeNotInBay = data.filter((j) => j.status !== "entregue");
      setJobs(activeNotInBay);
    });
  }, []);

  const filtered = jobs.filter((j) => {
    const s = q.toLowerCase();
    if (!s) return true;
    return (j.vehicle?.plate || "").toLowerCase().includes(s) ||
      (j.vehicle?.make || "").toLowerCase().includes(s) ||
      (j.vehicle?.model || "").toLowerCase().includes(s) ||
      (j.client_name || "").toLowerCase().includes(s);
  }).slice(0, 20);

  const submit = async () => {
    if (!jobId) { toast.error("Selecione um Job"); return; }
    setSaving(true);
    try {
      await api.patch(`/bays/${bayId}/assign`, { job_id: jobId, technician: technician || null });
      toast.success(`Alocado em ${bayName}`);
      onAssigned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao alocar"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="assign-bay-dialog">
      <DialogHeader><DialogTitle>Alocar Job em {bayName}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Buscar</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Placa, marca, modelo, cliente…" data-testid="assign-search" /></div>
        <div className="border border-border rounded max-h-64 overflow-y-auto divide-y divide-border">
          {filtered.length === 0 ? <div className="p-4 text-sm text-muted-foreground text-center">Nenhum job ativo.</div> :
            filtered.map((j) => (
              <button
                key={j.id}
                onClick={() => { setJobId(j.id); setTechnician(j.technician || ""); }}
                className={`w-full text-left px-3 py-2 hover:bg-accent ${jobId === j.id ? "bg-primary/10" : ""}`}
                data-testid={`assign-job-${j.id}`}
              >
                <div className="text-sm font-medium">{j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model} · <span className="font-mono text-muted-foreground">{j.vehicle?.plate || "—"}</span></div>
                <div className="text-xs text-muted-foreground">{j.client_name} · {j.service_type}</div>
              </button>
            ))}
        </div>
        <div><Label>Técnico responsável</Label><Input value={technician} onChange={(e) => setTechnician(e.target.value)} data-testid="assign-technician" /></div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !jobId} data-testid="assign-confirm">{saving ? "Alocando…" : "Alocar"}</Button>
        </DialogFooter>
      </div>
    </DialogContent>
  );
}
